import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { gridConfigFactory } from '~/lib/gridConfig';
import { useAgGrid } from '~/hooks/useAgGrid';
import { useColumnState } from '~/atoms/hooks/useColumnState';
import 'ag-grid-enterprise/styles/ag-grid.css';
import 'ag-grid-enterprise/styles/ag-theme-alpine.css';
import { Spinner } from '@nike/podium';
import { useGridData } from '~/atoms/hooks/useGridData';
import { useSearch } from '~/atoms/hooks/useSearch';
import { useSavedViews } from '~/atoms/hooks/useSavedViews';
import { useLinePlanEntity } from '~/atoms/hooks/useLinePlanEntity';
import { toPluralEntity } from '~/utils/linePlanEntity';
import log from '~/logger';
import { usePageActions } from '~/atoms/hooks/usePageActions';
const GridView = ({ className = '' }) => {
    const { gridData: models, gridDataLoadingState, error, setSelectedRows } = useGridData();
    const { linePlanEntity } = useLinePlanEntity();
    const { activeView } = useColumnState();
    const { savedViewsLoadingState } = useSavedViews();
    const [searchValue] = useSearch();
    const { setDetailSidebar } = usePageActions();
    const timeoutRef = useRef(null);
    const loading = gridDataLoadingState === 'loading';
    const viewsLoading = savedViewsLoadingState === 'loading';
    const { gridApi, onGridReady, onSelectionChanged: handleGridSelection, setQuickFilter, captureCurrentState, applyColumnState } = useAgGrid();
    const gridConfig = useMemo(() => {
        return gridConfigFactory.createProductGridConfig(models, linePlanEntity);
    }, [models, linePlanEntity]);
    useEffect(() => {
        if (setQuickFilter) {
            setQuickFilter(searchValue);
        }
    }, [searchValue, setQuickFilter]);
    useEffect(() => {
        if (!gridApi || !activeView?.metadata) {
            return;
        }
        try {
            const savedColumnState = activeView.metadata.gridState;
            applyColumnState(savedColumnState);
        }
        catch (error) {
            log.error('Error applying saved view column state:', error);
        }
    }, [gridApi, activeView, applyColumnState]);
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);
    const handleSelectionChanged = useCallback((event) => {
        handleGridSelection();
        const selectedRows = event.api.getSelectedRows();
        setSelectedRows(selectedRows);
    }, [setSelectedRows, handleGridSelection]);
    const handleColumnEvent = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
            captureCurrentState();
            timeoutRef.current = null;
        }, 500);
    }, [captureCurrentState]);
    const handleRowClick = useCallback((event) => {
        const rowData = event.data;
        if (rowData) {
            setDetailSidebar({
                isOpen: true,
                entity: rowData,
                entityType: linePlanEntity
            });
        }
    }, [setDetailSidebar, linePlanEntity]);
    return (_jsx("div", { className: `w-full h-full flex flex-col ${className}`, children: _jsx("div", { children: _jsxs("div", { className: "ag-theme-alpine relative min-h-9 h-[calc(100vh-280px)]", children: [viewsLoading && (_jsx("div", { className: "absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10", children: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Spinner, {}), _jsx("span", { className: "text-gray-600", children: "Loading saved views..." })] }) })), error && !loading && (_jsx("div", { className: "absolute inset-0 bg-white flex items-center justify-center z-10", children: _jsxs("div", { className: "text-center", children: [_jsx("p", { className: "text-xl text-gray-500 dark:text-gray-400 mb-2", children: `No ${toPluralEntity(linePlanEntity)} found` }), _jsx("p", { className: "text-sm text-gray-400 dark:text-gray-500", children: "Try adjusting your filters or search term" })] }) })), !viewsLoading && (_jsx(AgGridReact, { rowData: gridConfig.rowData, columnDefs: gridConfig.columnDefs, defaultColDef: gridConfig.defaultColDef, ...gridConfig.gridOptions, theme: "legacy", loading: loading, onGridReady: (params) => {
                            const savedViewState = activeView?.metadata;
                            onGridReady(params, savedViewState);
                        }, onSelectionChanged: handleSelectionChanged, onRowClicked: handleRowClick, onColumnResized: handleColumnEvent, onColumnMoved: handleColumnEvent, onColumnVisible: handleColumnEvent, onColumnPinned: handleColumnEvent, onSortChanged: handleColumnEvent, onFilterChanged: handleColumnEvent }, "product-grid"))] }) }) }));
};
export default GridView;
